import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import { enqueueSnackbar } from 'notistack';

import { type ArrayElement, type Defined } from '@/types/common';

import { gql } from '../../generated';

import { CASCADE_PROVIDER_FAMILY, useTransformedGateGroups } from './use-transformed-gate-groups';

export type UseActiveGateGroupProps = {
  filter?: UseActiveGateGroupFilter;
};

export type UseActiveGateGroupFilter = {
  currency_id?: string;
  gate_type_id?: 'invoice' | 'withdraw';
};

const GET_ACTIVE_GATE_GROUP = gql(/* GraphQL */ `
  query GetActiveGateGroup($where: gate_group_bool_exp) {
    gate_group(where: $where) {
      id
      name
      provider_family
      cascade_id
      active_gate {
        id
        gate_type_id
        currency_id
        alias
      }
    }
  }
`);

export type ActiveGateGroup = ArrayElement<Defined<ReturnType<typeof useActiveGateGroup>['data']>['gate_group']>;

export type UseActiveGateGroupData = ReturnType<typeof useActiveGateGroup>['data'];

export const useActiveGateGroup = (props: UseActiveGateGroupProps = {}) => {
  const { filter } = props;

  const result = useQuery(GET_ACTIVE_GATE_GROUP, {
    variables: {
      where: {
        _or: [
          {
            provider_family: {
              _eq: CASCADE_PROVIDER_FAMILY,
            },
          },
          {
            active_gate: {
              currency_id: {
                _eq: filter?.currency_id || undefined,
              },
              gate_type_id: filter?.gate_type_id
                ? {
                    _eq: filter?.gate_type_id,
                  }
                : undefined,
            },
          },
        ],
      },
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: 'error' });
    },
  });

  const transformedGateGroups = useTransformedGateGroups(result.data?.gate_group);

  return useMemo(
    () => ({
      ...result,
      data: result.data && {
        gate_group: transformedGateGroups,
      },
    }),
    [result, transformedGateGroups]
  );
};
